import axios from "axios";
import { API_BASE } from "./api";

const CHECK_KEY = "rp_controller_update_last_check";
const DEVICE_KEY = "rp_controller_update_device_id";
const SIX_HOURS_MS = 6 * 60 * 60 * 1000;

function nativeUpdateBridge() {
  if (typeof window === "undefined") return null;
  return window.RpSignageUpdate || null;
}

function stableControllerDeviceId() {
  let id = localStorage.getItem(DEVICE_KEY);
  if (!id) {
    const random = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`).toString();
    id = `controller-${random}`;
    localStorage.setItem(DEVICE_KEY, id);
  }
  return id;
}

async function postStatus(deviceId, oldVersionCode, newVersionCode, status, errorMessage = "") {
  try {
    await axios.post(`${API_BASE}/app-updates/status`, {
      deviceId,
      appType: "controller",
      oldVersionCode,
      newVersionCode,
      status,
      errorMessage,
    });
  } catch {
    // Status reporting should never block the controller app.
  }
}

export async function checkControllerAppUpdate({ force = false } = {}) {
  const bridge = nativeUpdateBridge();
  if (!bridge) return;

  const last = Number(localStorage.getItem(CHECK_KEY) || 0);
  if (!force && last && Date.now() - last < SIX_HOURS_MS) return;
  localStorage.setItem(CHECK_KEY, String(Date.now()));

  const deviceId = stableControllerDeviceId();
  const currentVersionCode = Number(bridge.getVersionCode?.() || 1);

  try {
    if (bridge.isWifiOrEthernet && !bridge.isWifiOrEthernet()) {
      await postStatus(deviceId, currentVersionCode, currentVersionCode, "failed", "WiFi or ethernet is required");
      return;
    }

    const { data } = await axios.get(`${API_BASE}/app-updates/check`, {
      params: { deviceId, appType: "controller", currentVersionCode },
      timeout: 15000,
    });
    if (!data?.updateAvailable) return;

    const title = data.forceUpdate ? "Mandatory RP Signage Controller update required." : "New RP Signage Controller update available. Install now?";
    const notes = data.releaseNotes ? `\n\n${data.releaseNotes}` : "";
    if (!data.forceUpdate && !window.confirm(`${title}${notes}`)) return;

    if (bridge.canInstallPackages && !bridge.canInstallPackages()) {
      window.alert("Please allow RP Signage Controller to install updates, then try again.");
      bridge.openInstallPermissionSettings?.();
      await postStatus(deviceId, currentVersionCode, data.latestVersionCode, "failed", "Install unknown apps permission required");
      return;
    }

    await postStatus(deviceId, currentVersionCode, data.latestVersionCode, "downloading");
    bridge.downloadAndInstall(
      data.apkUrl,
      data.sha256 || "",
      `${API_BASE}/app-updates/status`,
      deviceId,
      currentVersionCode,
      Number(data.latestVersionCode || 0),
      !!data.forceUpdate,
    );
  } catch (error) {
    await postStatus(deviceId, currentVersionCode, currentVersionCode, "failed", error?.message || "Update check failed");
  }
}
